/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import TripTitleInput from '../components/TripTitleInput';
import TripDatePager from '../components/TripDatePager';
import TripItinerary from '../components/TripItinerary';
import TripItineraryDatesFiltersContainer from './TripItineraryDatesFiltersContainer';
import TripPlannerFiltersContainer from './TripPlannerFiltersContainer';
import * as tripPlannerActions from '../actions/tripPlanner';
import {
    getDestinationIndexForDate,
    getDateForDestinationIndex,
    getTripStartEndDates,
    isTripEditable,
} from '../utils/tripUtils';
import TripShareModal from '../components/TripShareModal';

const propTypes = {
    router: PropTypes.any,
    tripPlanner: PropTypes.any,
    dispatch: PropTypes.func,
    trip: PropTypes.object,
    lastSavedDate: PropTypes.string,
};

export class TripItineraryContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            mounted: false,
        };

        this.onMoveUpDestination = this.onMoveUpDestination.bind(this);
        this.onMoveDownDestination = this.onMoveDownDestination.bind(this);
        this.onDeleteDestination = this.onDeleteDestination.bind(this);
        this.onBookTripItem = this.onBookTripItem.bind(this);
        this.onDeleteTripItem = this.onDeleteTripItem.bind(this);
        this.onChangeTripName = this.onChangeTripName.bind(this);
        this.onDateSelected = this.onDateSelected.bind(this);
        this.onDestinationSelected = this.onDestinationSelected.bind(this);
        this.onClickShareTrip = this.onClickShareTrip.bind(this);
        this.onTripItemSelected = this.onTripItemSelected.bind(this);
        this.onClickPrintTrip = this.onClickPrintTrip.bind(this);

        this.initSelectedTripDates(props.trip);
    }

    componentDidMount() {
        window.scrollTo(0, 0);

        this.setState(
            {
                mounted: true,
            },
            () => {
                this.scheduleAutoRefresh();
            }
        );
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.trip && this.props.trip.trip_id !== nextProps.trip.trip_id) {
            this.initSelectedTripDates(nextProps.trip);
        }
    }

    componentWillUnmount() {
        this.setState({
            mounted: false,
        });
    }

    initSelectedTripDates(trip) {
        if (trip) {
            const tripDates = getTripStartEndDates(trip, true);
            this.props.dispatch(tripPlannerActions.setActiveDate(tripDates[0]));
        }
    }

    onMoveUpDestination(destinationIndex) {
        if (destinationIndex > 1) {
            this.props.dispatch(
                tripPlannerActions.moveDestination(destinationIndex, destinationIndex - 1)
            );
            this.refreshRoute();
        }
    }

    onMoveDownDestination(destinationIndex) {
        if (destinationIndex < this.props.trip.destinations.length - 1) {
            this.props.dispatch(
                tripPlannerActions.moveDestination(destinationIndex, destinationIndex + 1)
            );
            this.refreshRoute();
        }
    }

    onDeleteDestination(destinationIndex) {
        this.props.dispatch(tripPlannerActions.deleteDestination(destinationIndex));
        this.refreshRoute();
    }

    onBookTripItem(destinationIndex, item) {
        // show modal
        const modalData = {
            timestamp: Date.now(),
            item: { ...item, guestCounts: this.props.trip.guest_counts },
        };

        this.props.dispatch(tripPlannerActions.setDataBookModal(modalData));
    }

    onClickShareTrip() {
        this.props.dispatch(tripPlannerActions.setShowTripShareModal(true));
    }

    onClickPrintTrip() {
        this.props.dispatch(tripPlannerActions.showTripPDF(this.props.trip.trip_id));
    }

    onDeleteTripItem(destinationIndex, item) {
        this.props.dispatch(tripPlannerActions.deleteItem(destinationIndex, item));
    }

    onChangeTripName(newTripName) {
        this.props.dispatch(
            tripPlannerActions.setName(newTripName, this.props.router.navigate)
        );
    }

    onDateSelected(activeDate) {
        this.props.dispatch(tripPlannerActions.setActiveDate(activeDate));
        this.props.dispatch(
            tripPlannerActions.setActiveDestinationIndex(
                getDestinationIndexForDate(this.props.trip, activeDate),
                true,
                true,
                false
            )
        );
    }

    onDestinationSelected(destinationIndex) {
        this.props.dispatch(
            tripPlannerActions.setActiveDate(
                getDateForDestinationIndex(this.props.trip, destinationIndex)
            )
        );
        this.props.dispatch(
            tripPlannerActions.setActiveDestinationIndex(
                destinationIndex,
                true,
                true,
                false
            )
        );
    }

    onTripItemSelected(destinationIndex, itemIndex) {
        this.props.dispatch(
            tripPlannerActions.setActiveDate(
                getDateForDestinationIndex(this.props.trip, destinationIndex)
            )
        );
        this.props.dispatch(
            tripPlannerActions.setActiveItineraryItemIndex(destinationIndex, itemIndex)
        );
    }

    refreshRoute() {
        setTimeout(() => {
            this.props.dispatch(
                tripPlannerActions.fetchDrivingDirections(this.props.trip)
            );
            this.props.dispatch(tripPlannerActions.fetchDetailedRoute(this.props.trip));
        }, 300);
    }

    scheduleAutoRefresh() {
        if (this.state.mounted) {
            setTimeout(() => {
                this.props.dispatch(tripPlannerActions.fetchTripItemsDetails());
                this.scheduleAutoRefresh();
            }, 30000);
        }
    }

    onApplyFilters = () => {
        this.props.dispatch(tripPlannerActions.fetchSearchFiltersAggregations());
    };

    render() {
        const editable = isTripEditable(this.props.trip);
        const tripDates = getTripStartEndDates(this.props.trip, true);

        return (
            <div
                className="trip-planner-itinerary-container"
                data-testid="trip-planner-itinerary-container"
            >
                <div className="trip-planner-itinerary-inner-wrap">
                    <div className="trip-planner-itinerary-top-section-wrap">
                        <TripTitleInput
                            tripName={this.props.trip.name}
                            editable={editable}
                            onChangeTripName={this.onChangeTripName}
                            onClickShareTrip={this.onClickShareTrip}
                            onClickPrintTrip={this.onClickPrintTrip}
                            lastSavedDate={this.props.lastSavedDate}
                        />

                        <TripItineraryDatesFiltersContainer
                            startDate={tripDates[0]}
                            endDate={tripDates[1]}
                        />

                        {!this.props.tripPlanner.filtersVisible && (
                            <TripDatePager
                                activeDate={this.props.tripPlanner.activeDate}
                                startDate={tripDates[0]}
                                endDate={tripDates[1]}
                                onDateSelected={this.onDateSelected}
                            />
                        )}
                    </div>
                    {!this.props.tripPlanner.filtersVisible && (
                        <TripItinerary
                            trip={this.props.trip}
                            activeDate={this.props.tripPlanner.activeDate}
                            activeDestinationItineraryIndex={
                                this.props.tripPlanner.activeDestinationItineraryIndex
                            }
                            onMoveUpDestination={this.onMoveUpDestination}
                            onMoveDownDestination={this.onMoveDownDestination}
                            onDeleteDestination={this.onDeleteDestination}
                            onBookTripItem={this.onBookTripItem}
                            onDeleteTripItem={this.onDeleteTripItem}
                            onDestinationSelected={this.onDestinationSelected}
                            onTripItemSelected={this.onTripItemSelected}
                            editable={editable}
                        />
                    )}
                    {this.props.tripPlanner.filtersVisible && (
                        <TripPlannerFiltersContainer
                            onApplyFilters={this.onApplyFilters}
                        />
                    )}
                    <TripShareModal />
                </div>
            </div>
        );
    }
}

TripItineraryContainer.propTypes = propTypes;

function select(state) {
    return {
        tripPlanner: state.tripPlanner,
        trip: state.tripPlanner.trip,
        lastSavedDate: state.tripPlanner.lastSavedDate,
    };
}

export default withCompatRouter(connect(select)(TripItineraryContainer));
